/** @jsxRuntime classic */
/** @jsx h */
import { AutocompletePlugin } from '@algolia/autocomplete-js';
import { h } from 'preact';
import { normalizeForCompare } from './utils/queryNormalization';
import { toAlgoliaHighlightTokens } from './utils/highlightTokens';

export type SuggestionItem = {
  id?: string;
  hash_id?: string;
  synthetic?: boolean;
  collection?: string;
  query: string;
  label: string;
  url?: string;
  highlight?: Record<string, { snippet?: string }>;
  _highlightResult?: {
    query?: {
      value: string;
      matchLevel?: string;
      matchedWords?: string[];
    };
  };
};

type CreateTypesenseSuggestionsPluginParams = {
  endpoint: string;
  defaultsEndpoint: string;
  searchResultsUrl: string;
  perPage: number;
  fillQueryTitle?: string;
  defaultsLang: string;
  defaultsTtl: number;
  mobileDebounceMs: number;
  desktopDebounceMs: number;
  isMobileDevice: boolean;
  initialQuery?: string;
  getRecentItems?: (
    query: string
  ) => Array<{ label?: string; query?: string; normalized?: string }>;
};

// Shared cache across plugin instances so repeated init cycles don't refetch identical defaults.
const sharedDefaultsMemoryCache = new Map<string, SuggestionItem[]>();
// Single-flight registry to collapse concurrent defaults requests into one network call.
const sharedDefaultsInFlight = new Map<string, Promise<SuggestionItem[]>>();

function dedupeByRecent(
  items: SuggestionItem[],
  recentItems: Array<{ label?: string; query?: string; normalized?: string }>
) {
  if (!recentItems.length || !items.length) {
    return items;
  }

  const recentOriginal = new Set<string>();
  const recentNormalized = new Set<string>();

  recentItems.forEach((item) => {
    const original = String(item?.label || item?.query || '').trim().toLowerCase();
    const normalized = String(item?.normalized || '');

    if (original) {
      recentOriginal.add(original);
    }
    if (normalized) {
      recentNormalized.add(normalized);
    }
  });

  return items.filter((item) => {
    const original = String(item?.label || item?.query || '').trim().toLowerCase();
    const normalized = normalizeForCompare(original);

    if (!original) {
      return true;
    }

    return !recentOriginal.has(original) && !recentNormalized.has(normalized);
  });
}

function debouncePromise<TParams extends unknown[], TResult>(
  fn: (...params: TParams) => Promise<TResult> | TResult,
  time: number
) {
  let timerId: ReturnType<typeof setTimeout> | undefined;

  return function (...args: TParams) {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise<TResult>((resolve) => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}

function normalizeResultsPayload(data: any): SuggestionItem[] {
  const results = Array.isArray(data?.results) ? data.results : [];
  const items: SuggestionItem[] = [];

  results.forEach((result: any) => {
    const collection =
      result?.request_params?.collection_name ?? result?.request_params?.collection ?? '';

    if (collection !== 'suggestions') {
      return;
    }

    (result?.hits ?? []).forEach((hit: any) => {
      const label = hit?.document?.q ?? '';
      if (!label) {
        return;
      }

      const id = String(hit?.document?.id ?? '').trim();

      items.push({
        id: id || undefined,
        hash_id: id || undefined,
        collection: 'suggestions',
        query: label,
        label,
        highlight: hit?.highlight ?? {},
        _highlightResult: {
          query: {
            value: toAlgoliaHighlightTokens(hit?.highlight?.q?.snippet || label),
            matchLevel: 'none',
            matchedWords: [],
          },
        },
      });
    });
  });

  return items;
}

function fetchSuggestions(endpoint: string, query: string, perPage: number) {
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('per_page', String(perPage));

  return fetch(url.toString())
    .then((response) => {
      if (!response.ok) {
        throw new Error('Bad response');
      }
      return response.json();
    })
    .then(normalizeResultsPayload)
    .catch(() => [] as SuggestionItem[]);
}

function readDefaultsCache(cacheKey: string, ttlSeconds: number, limit: number): SuggestionItem[] {
  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt ?? 0);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];

    if (!savedAt || ttlSeconds <= 0) {
      return [];
    }

    if (Date.now() - savedAt > ttlSeconds * 1000) {
      return [];
    }

    return items.slice(0, limit);
  } catch (_error) {
    return [];
  }
}

function writeDefaultsCache(cacheKey: string, items: SuggestionItem[], limit: number) {
  try {
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        savedAt: Date.now(),
        items: items.slice(0, limit),
      })
    );
  } catch (_error) {
  }
}

function buildSearchUrl(baseUrl: string, query: string) {
  try {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('q', query || '');
    return url.toString();
  } catch (_error) {
    return `${baseUrl}?q=${encodeURIComponent(query || '')}`;
  }
}

function collapseWhitespace(value: string) {
  return String(value || '').trim().replace(/\s+/g, ' ');
}

async function createSyntheticSuggestion(query: string): Promise<SuggestionItem | null> {
  const label = collapseWhitespace(query);
  if (!label) {
    return null;
  }

  return {
    synthetic: true,
    collection: 'suggestions',
    query: label,
    label,
    _highlightResult: {
      query: {
        value: label,
        matchLevel: 'none',
        matchedWords: [],
      },
    },
  };
}

export function createTypesenseSuggestionsPlugin(
  options: CreateTypesenseSuggestionsPluginParams
): AutocompletePlugin<SuggestionItem, undefined> {
  const {
    endpoint,
    defaultsEndpoint,
    searchResultsUrl,
    perPage,
    fillQueryTitle,
    defaultsLang,
    defaultsTtl,
    mobileDebounceMs,
    desktopDebounceMs,
    isMobileDevice,
    initialQuery,
    getRecentItems,
  } = options;

  const selectedDebounceMs = isMobileDevice
    ? mobileDebounceMs
    : desktopDebounceMs;

  const defaultsCacheKey = `typesense_defaults_cache:${defaultsEndpoint}:${defaultsLang}`;
  // Include perPage in the key because mobile/desktop can request different defaults sizes.
  const defaultsRequestKey = `${defaultsCacheKey}:${perPage}`;
  let defaultsMemoryCache: SuggestionItem[] = [];

  if (defaultsEndpoint) {
    const sharedCached = sharedDefaultsMemoryCache.get(defaultsRequestKey);
    defaultsMemoryCache = sharedCached && sharedCached.length
      ? sharedCached
      : readDefaultsCache(defaultsCacheKey, defaultsTtl, perPage);

    if (defaultsMemoryCache.length) {
      sharedDefaultsMemoryCache.set(defaultsRequestKey, defaultsMemoryCache);
    }
  }

  const loadDefaults = () => {
    if (!defaultsEndpoint) {
      return Promise.resolve([] as SuggestionItem[]);
    }

    if (defaultsMemoryCache.length) {
      return Promise.resolve(defaultsMemoryCache);
    }

    // Reuse an existing in-flight request for the same key to prevent duplicate startup calls.
    const sharedInFlight = sharedDefaultsInFlight.get(defaultsRequestKey);
    if (sharedInFlight) {
      return sharedInFlight;
    }

    const inFlight = fetchSuggestions(defaultsEndpoint, '*', perPage)
      .then((items) => {
        const top = items.slice(0, perPage);
        defaultsMemoryCache = top;
        sharedDefaultsMemoryCache.set(defaultsRequestKey, top);
        writeDefaultsCache(defaultsCacheKey, top, perPage);
        return top;
      })
      .catch(() => [] as SuggestionItem[])
      .finally(() => {
        sharedDefaultsInFlight.delete(defaultsRequestKey);
      });

    // Register request before returning so parallel callers join this same promise.
    sharedDefaultsInFlight.set(defaultsRequestKey, inFlight);
    return inFlight;
  };

  void loadDefaults();

  // Prefetch suggestions for the initial query so first focus is instant.
  let prefetchedQuery = '';
  let prefetchedItems: SuggestionItem[] | null = null;
  const trimmedInitial = (initialQuery || '').trim();
  if (trimmedInitial && endpoint) {
    prefetchedQuery = trimmedInitial.toLowerCase();
    fetchSuggestions(endpoint, trimmedInitial, perPage).then((items) => {
      prefetchedItems = items;
    });
  }

  const debounced = debouncePromise(
    (items: any[]) => Promise.resolve(items),
    selectedDebounceMs
  );

  return {
    name: 'typesenseSuggestionsPlugin',
    getSources({ query, setQuery, refresh }) {
      const trimmed = query.trim();

      const sources = [
        {
          sourceId: 'querySuggestionsPlugin',
          getItemInputValue({ item }: { item: SuggestionItem }) {
            return item.label;
          },
          getItemUrl({ item }: { item: SuggestionItem }) {
            return buildSearchUrl(searchResultsUrl, item?.label || '');
          },
          getItems() {
            const recentItems = getRecentItems?.(trimmed) ?? [];

            if (!trimmed) {
              if (defaultsMemoryCache.length) {
                return Promise.resolve(
                  dedupeByRecent(defaultsMemoryCache.slice(0, perPage), recentItems)
                );
              }

              return loadDefaults().then((items) =>
                dedupeByRecent(items.slice(0, perPage), recentItems)
              );
            }

            // Return prefetched results instantly if query matches.
            if (
              prefetchedItems
              && trimmed.toLowerCase() === prefetchedQuery
            ) {
              return Promise.resolve(
                dedupeByRecent(prefetchedItems.slice(0, perPage), recentItems)
              );
            }

            return fetchSuggestions(endpoint, trimmed, perPage)
              .then((items) => dedupeByRecent(items.slice(0, perPage), recentItems))
              .then(async (items) => {
                if (items.length > 0) {
                  return items;
                }

                const syntheticItem = await createSyntheticSuggestion(trimmed);
                return syntheticItem ? [syntheticItem] : [];
              });
          },
          onSelect({ item }: { item: SuggestionItem }) {
            if (!item?.label) {
              return;
            }
            window.location.assign(buildSearchUrl(searchResultsUrl, item.label));
          },
          templates: {
            item({ item, components }: { item: SuggestionItem; components: any }) {
              return (
                <div className="aa-ItemWrapper">
                  <div className="aa-ItemContent">
                    <div className="aa-ItemIcon aa-ItemIcon--noBorder">
                      {!trimmed ? (
                        <svg viewBox="0 -960 960 960" fill="currentColor" aria-hidden="true">
                          <path d="m136-240-56-56 296-298 160 160 208-206H640v-80h240v240h-80v-104L536-320 376-480 136-240Z" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z" />
                        </svg>
                      )}
                    </div>
                    <div className="aa-ItemContentBody">
                      <div className="aa-ItemContentTitle">
                        <components.ReverseHighlight hit={item} attribute="query" />
                      </div>
                    </div>
                  </div>
                  <div className="aa-ItemActions">
                    <button
                      type="button"
                      className="aa-ItemActionButton"
                      title={fillQueryTitle || undefined}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setQuery(item.label);
                        refresh();
                      }}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 17v-7.586l8.293 8.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-8.293-8.293h7.586c0.552 0 1-0.448 1-1s-0.448-1-1-1h-10c-0.552 0-1 0.448-1 1v10c0 0.552 0.448 1 1 1s1-0.448 1-1z" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            },
          },
        },
      ];

      // Skip debounce when prefetched data is ready — no point waiting.
      const hasPrefetched = prefetchedItems !== null
        && trimmed.toLowerCase() === prefetchedQuery;
      return trimmed && selectedDebounceMs > 0 && !hasPrefetched
        ? debounced(sources)
        : sources;
    },
  };
}
